/**
 * Search page — Pages & Articles slider, fetched separately from the product grid.
 *
 * The results page requests `type=product` so the product grid paginates cleanly: every page gets
 * a full `grid_items` of tiles. Previously all three result types shared one paginated stream and
 * the grid loop skipped the non-products, so a 15-per-page slice could render 14 or 13 cards.
 *
 * The cost is that Liquid can no longer see articles or pages — `search` is one object per request,
 * scoped by the URL's `type`. So this re-renders THE SAME SECTION with `type=article,page` and
 * lifts the finished slider out of the response.
 *
 * Deliberately a section render rather than building cards from `/search/suggest.json`: it keeps
 * sections/search.liquid the single source of that markup, so the Blaze structure, the pa-card
 * image fallbacks and the brand-logo placeholder can't drift from the Liquid version.
 *
 * Hook: [data-search-pa] with data-search-pa-terms and data-search-pa-section.
 */
const HOOK = "[data-search-pa]";
const SLIDER = ".pages-articles-slider";

async function fill(mount) {
  const terms = mount.dataset.searchPaTerms;
  const sectionId = mount.dataset.searchPaSection;
  if (!terms || !sectionId) return;

  const url = new URL(window.location.origin + window.location.pathname);
  url.searchParams.set("q", terms);
  url.searchParams.set("type", "article,page");
  url.searchParams.set("section_id", sectionId);

  const response = await fetch(url.toString(), {
    headers: { "X-Requested-With": "XMLHttpRequest" },
    credentials: "same-origin",
  });
  if (!response.ok) return;

  const doc = new DOMParser().parseFromString(await response.text(), "text/html");
  const slider = doc.querySelector(SLIDER);
  // No articles or pages matched — the mount just stays empty, which renders as nothing.
  if (!slider) return;

  mount.replaceWith(slider);

  // The Blaze controller watches for sliders added after load (critical.js registers it with
  // `observe: true`), so the injected markup is picked up without being told. Nudge it anyway if
  // the controller is already on the page, since its observer may have disconnected after its
  // first successful load.
  window.themeBlazeSliderController?.refresh?.();
}

export function initSearchPagesArticles(root = document) {
  const scope = root && typeof root.querySelectorAll === "function" ? root : document;

  scope.querySelectorAll(HOOK).forEach((mount) => {
    if (mount.dataset.searchPaLoaded === "true") return;
    mount.dataset.searchPaLoaded = "true";
    // Failure is silent and non-fatal: the product results are the page's job, and a missing
    // Pages & Articles row is better than a broken one.
    fill(mount).catch(() => {});
  });
}

export default initSearchPagesArticles;
